import { SagaIterator } from "redux-saga";
import { all, call, put, select, takeEvery } from "redux-saga/effects";

import { IAgentFormRow } from "components/agents-limit-reached";
import { Right } from "constants/right";
import Role from "constants/role";
import { ToastContent, getToastContent } from "constants/toasts";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { canManageAgentPermission, canManageAgentSignature, canManageAgentTeams } from "helpers/rights";
import { pushWithUTM } from "helpers/sagas";
import { AgentsRoutes } from "routes/agents/constants";
import { ApiManager } from "services/api";
import { deserializeLiveChatAgents } from "services/api/integrations/integrations-serializer";
import { CRUDAction, IEntityDeleteFailure, RequestAction } from "store/entities/actions";
import {
  AGENT,
  AgentActions,
  IAgent,
  IBatchCreateAgentsFailurePayload,
  selectAgent,
  selectIsAdmin,
} from "store/entities/agents";
import { selectHasLiveChatIntegration } from "store/entities/integrations";
import { selectAutoassignmentManagedByAgents } from "store/entities/license";
import { getCurrentAgentId, hasRight } from "store/features/session";
import { ToastAutoHideDelay, ToastVariant, ToastsActions } from "store/features/toasts";
import { IActionWithPayload } from "store/helper";

import { WelcomeTutorialRequestSource } from "../welcome-tutorial/sagas";

import { AgentsViewActions, AgentsViewActionsNames } from "./actions";
import {
  IAgentBatchCreatePayload,
  IAgentDeletePayload,
  IAgentUpdatePayload,
  IAgentUpdateSuccessPayload,
  IImportLiveChatAgentFailurePayload,
  IImportLiveChatAgentPayload,
} from "./interfaces";

const AgentsRequestSource = "agents";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* batchCreateAgents(action: IActionWithPayload<string, IAgentBatchCreatePayload>) {
  const payload = { ...action.payload };

  const mappedPayload = {
    agents: payload.agents.map((agent: IAgentFormRow) => ({
      name: agent.name,
      email: agent.email,
      teamIds: agent.teams,
      permission: agent.permission,
    })),
    requestSource: AgentsRequestSource,
  };

  // @ts-expect-error TS(2345)
  yield put(AgentActions.batchCreateAgents(mappedPayload));
}

function* batchCreateAgentsSuccess(action: IActionWithPayload<string, IAgentBatchCreatePayload>): SagaIterator {
  const payload = { ...action.payload };
  yield call(trackEngagement, "Agents: Agent added", EventPlace.Agents, {
    numberOfAgentsInvited: payload.agents.length,
  });
  if (payload.requestSource === WelcomeTutorialRequestSource) {
    return;
  }
  yield put(pushWithUTM(AgentsRoutes.Agents));
}

function* batchCreateAgentsFailure(action: IActionWithPayload<string, IBatchCreateAgentsFailurePayload>): SagaIterator {
  const payload = { ...action.payload };
  if (payload.requestSource === AgentsRequestSource) {
    yield put(
      ToastsActions.createToast({
        content: getToastContent(ToastContent.AGENTS_ADD_FAILURE, payload),
        variant: ToastVariant.Error,
        error: payload?.errorObject?.error,
      })
    );
  }
}

function* updateAgent(action: IActionWithPayload<string, IAgentUpdatePayload>): SagaIterator {
  const payload = { ...action.payload };
  const agentId = payload.id;

  if (!agentId) return;

  const isAdmin = yield select(selectIsAdmin);
  const isAutoAssignmentManagedByAgents = yield select(selectAutoassignmentManagedByAgents);
  const currentAgentId = yield select(getCurrentAgentId);
  const canManageAgents = yield select(hasRight, Right.ManageAgents);
  const canManageOwnSignature = yield select(hasRight, Right.ManageOwnSignature);
  const canManagePermission = canManageAgentPermission(agentId, currentAgentId, canManageAgents);
  const canManageTeams = canManageAgentTeams(canManageAgents);
  const canChangeSignature = canManageAgentSignature(agentId, currentAgentId, canManageAgents, canManageOwnSignature);
  const unmodifiedAgent: IAgent = yield select(selectAgent, agentId);

  if (!canManagePermission || unmodifiedAgent.permission === payload.permission) {
    delete payload.permission;
  }

  if (!canManageTeams) {
    delete payload.teamIds;
  }

  if (!canChangeSignature) {
    delete payload.signature;
  }

  if (
    payload.permission === Role.Viewer ||
    payload.status === "invited" ||
    (!isAutoAssignmentManagedByAgents && !isAdmin)
  ) {
    delete payload.autoassignment;
    delete payload.autoassignmentTeamIDs;
  }

  yield put(AgentActions.update({ value: payload }));
}

function* updateAgentSuccess(action: IActionWithPayload<string, IAgentUpdateSuccessPayload>): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.AGENT_UPDATE_SUCCESS),
      variant: ToastVariant.Success,
    })
  );

  const agentId = action.payload.id;
  yield put(pushWithUTM(`/agents/${agentId}`));
}

function* deleteAgent(action: IActionWithPayload<string, IAgentDeletePayload>): SagaIterator {
  const payload = { ...action.payload };
  yield put(AgentActions.delete({ id: payload.agentId }));
  yield call(trackEngagement, "Agents: Agent deleted", EventPlace.Agents);
}

function* deleteAgentSuccess(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.AGENT_DELETE_SUCCESS),
      variant: ToastVariant.Success,
    })
  );

  yield put(pushWithUTM(AgentsRoutes.Agents));
}

function* deleteAgentFailure(
  action: IActionWithPayload<string, IEntityDeleteFailure & { isOwnerDeletion?: boolean }>
): SagaIterator {
  const { isOwnerDeletion } = action.payload;

  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.AGENT_DELETE_FAILURE, { isOwnerDeletion }),
      variant: ToastVariant.Error,
    })
  );

  yield put(pushWithUTM(AgentsRoutes.Agents));
}

function* fetchLiveChatAgents(): SagaIterator {
  const hasLiveChatIntegration = yield select(selectHasLiveChatIntegration);
  if (hasLiveChatIntegration) {
    const response = yield call(ApiManager.integrationsApi.fetchLiveChatAgents);
    if (response.result) {
      yield put(
        AgentsViewActions.fetchLiveChatAgentsSuccess({
          values: deserializeLiveChatAgents(response.result),
        })
      );
    }
  } else {
    yield put(
      AgentsViewActions.fetchLiveChatAgentsSuccess({
        // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'ILiveChatAg... Remove this comment to see the full error message
        values: null,
      })
    );
  }
}

export function* importLiveChatAgent(action: IActionWithPayload<string, IImportLiveChatAgentPayload>): SagaIterator {
  const { email, role } = action.payload;

  const { result, error } = yield call(ApiManager.integrationsApi.importLiveChatAgent, email, role);
  if (result) {
    yield put(AgentsViewActions.importLiveChatAgentSuccess());
  } else {
    yield put(AgentsViewActions.importLiveChatAgentFailure({ error, errorObject: error.error }));
  }
}

export function* importLiveChatAgentSuccess(): SagaIterator {
  yield call(trackEngagement, "Agents: LiveChat agent invited", EventPlace.Agents);
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.AGENTS_IMPORT_LIVECHAT_AGENT_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

export function* importLiveChatAgentFailure(
  action: IActionWithPayload<string, IImportLiveChatAgentFailurePayload>
): SagaIterator {
  const seatsLimitExceeded = action.payload.errorObject?.type === "limitExceeded";
  const paymentIssue = action.payload.errorObject?.type === "paymentIssue";

  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.AGENTS_IMPORT_LIVECHAT_AGENT_FAILURE, action.payload),
      variant: ToastVariant.Error,
      autoHideDelayTime: seatsLimitExceeded || paymentIssue ? ToastAutoHideDelay.Infinite : ToastAutoHideDelay.Long,
    })
  );
}

export function* agentsViewSagas(): SagaIterator {
  yield all([
    takeEvery(AgentsViewActionsNames.BATCH_CREATE_AGENTS, batchCreateAgents),
    takeEvery(AGENT.CREATE_BATCH[RequestAction.SUCCESS], batchCreateAgentsSuccess),
    takeEvery(AGENT.CREATE_BATCH[RequestAction.FAILURE], batchCreateAgentsFailure),
    takeEvery(AgentsViewActionsNames.UPDATE_AGENT, updateAgent),
    takeEvery(AGENT[CRUDAction.UPDATE][RequestAction.SUCCESS], updateAgentSuccess),
    takeEvery(AgentsViewActionsNames.DELETE_AGENT, deleteAgent),
    takeEvery(AGENT[CRUDAction.DELETE][RequestAction.SUCCESS], deleteAgentSuccess),
    takeEvery(AGENT[CRUDAction.DELETE][RequestAction.FAILURE], deleteAgentFailure),
    takeEvery(AgentsViewActionsNames.FETCH_LIVECHAT_AGENTS, fetchLiveChatAgents),
    takeEvery(AgentsViewActionsNames.IMPORT_LIVECHAT_AGENT[RequestAction.REQUEST], importLiveChatAgent),
    takeEvery(AgentsViewActionsNames.IMPORT_LIVECHAT_AGENT[RequestAction.SUCCESS], importLiveChatAgentSuccess),
    takeEvery(AgentsViewActionsNames.IMPORT_LIVECHAT_AGENT[RequestAction.FAILURE], importLiveChatAgentFailure),
  ]);
}
